import { useCallback, useEffect, useState } from 'react';
import type { SlackReviewRoute } from '@reef/schema';
import { api, type ReviewItem } from '../api.ts';

/**
 * What has stopped and is waiting on a person.
 *
 * Reef could always pause a step for approval; until now the only place to
 * decide one was Slack, which meant a deployment without Slack could produce
 * approvals it could never resolve — the step waited forever on a message
 * nobody received.
 *
 * The screen is built around the two things a reviewer actually does. They
 * approve, and the agent carries on. Or the plan is *nearly* right and they say
 * what to change — which is a different action from rejecting it, because
 * rejection throws away the eighty percent that was fine and asks for the whole
 * thing again.
 *
 * The plan itself leads. A queue that showed "step 4 wants approval" with the
 * proposal a click away is a queue people approve without reading, and an
 * approval nobody read is worse than no gate at all: it launders the decision.
 *
 * The one setting on the screen is where these reach you in Slack, and it is
 * here rather than on a settings page for a reason: this is the moment somebody
 * thinks "I should have been told about that". A preference screen they have to
 * go looking for is one they set after the second time it happens.
 */
export function ReviewQueueView({ onDecided }: { onDecided?: () => void }) {
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [hidden, setHidden] = useState(0);
  const [revising, setRevising] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decided, setDecided] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await api.reviewQueue();
      setItems(result.items);
      setHidden(result.hidden);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const act = async (item: ReviewItem, action: 'approve' | 'revise') => {
    setBusy(item.id);
    setError(null);

    try {
      if (action === 'approve') {
        await api.approveReview(item.id);
        setDecided(`Approved — ${label(item)} is carrying on.`);
      } else {
        await api.reviseReview(item.id, feedback.trim());
        setDecided(`Sent back to ${label(item)} with your note.`);
        setRevising(null);
        setFeedback('');
      }
      await refresh();
      onDecided?.();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Review queue</span>
        </span>
        <span className="spacer" />
        {items.length > 0 && <span className="chip warn">{items.length} waiting</span>}
        <button onClick={() => void refresh()} disabled={busy !== null}>
          Refresh
        </button>
      </header>

      <div className="studio-column wide">
        {error && <div className="banner danger">{error}</div>}
        {decided && !error && <div className="banner">{decided}</div>}

        <ReviewRoute />

        {items.length === 0 ? (
          <section className="studio-card">
            <div className="studio-card-head">
              <h2>Nothing is waiting on you</h2>
            </div>
            <div className="studio-card-body">
              <p className="hint" style={{ marginTop: 0 }}>
                A step lands here when it asks for approval before committing its work. Nothing has
                asked.
              </p>
              {hidden > 0 && (
                /*
                 * Said plainly rather than omitted. Somebody whose colleague can
                 * see three items and who sees none needs to know the difference
                 * is permission, not a bug — otherwise this screen gets reported
                 * as broken.
                 */
                <p className="hint">
                  {hidden} item{hidden === 1 ? ' is' : 's are'} waiting on workflows you have not
                  been shared on.
                </p>
              )}
            </div>
          </section>
        ) : (
          items.map((item) => (
            <section className="studio-card" key={item.id}>
              <div className="studio-card-head">
                <h2>{label(item)}</h2>
                <span className="chip">{item.workflowName}</span>
                {item.channel && <span className="chip">also in {item.channel}</span>}
              </div>

              <div className="studio-card-body">
                {/*
                  The plan first, at full width, before any control. The order is
                  the argument: a reviewer reads and then decides, and a layout
                  that puts the buttons above the proposal invites the opposite.
                */}
                <pre className="studio-instructions">
                  {item.summary ?? 'This step asked for approval without saying what for.'}
                </pre>

                {item.details && <pre className="studio-instructions">{item.details}</pre>}

                <p className="hint">
                  Waiting since {new Date(item.createdAt).toLocaleString()} · run{' '}
                  <code>{item.runId}</code> · ${(item.spendCents / 100).toFixed(2)} spent so far
                </p>

                {revising === item.id ? (
                  <div className="stack">
                    <div className="field">
                      <label htmlFor={`revise-${item.id}`}>What should it change?</label>
                      <textarea
                        id={`revise-${item.id}`}
                        value={feedback}
                        onChange={(event) => setFeedback(event.target.value)}
                        rows={4}
                        placeholder="Use the staging database rather than production, and add a test for the empty case."
                      />
                    </div>
                    <p className="hint" style={{ marginTop: 0 }}>
                      This goes to the step as written. It revises against your note and asks again,
                      so say what is wrong rather than that something is.
                    </p>
                    <div className="studio-row">
                      <button
                        onClick={() => void act(item, 'revise')}
                        disabled={busy === item.id || feedback.trim().length < 3}
                      >
                        Send it back
                      </button>
                      <button
                        className="ghost"
                        onClick={() => {
                          setRevising(null);
                          setFeedback('');
                        }}
                        disabled={busy === item.id}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="studio-row">
                    <button
                      className="approve"
                      onClick={() => void act(item, 'approve')}
                      disabled={busy === item.id}
                    >
                      {busy === item.id ? 'Approving…' : 'Approve'}
                    </button>
                    <button
                      onClick={() => {
                        setRevising(item.id);
                        setFeedback('');
                        setDecided(null);
                      }}
                      disabled={busy === item.id}
                    >
                      Request changes
                    </button>
                  </div>
                )}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

/** The step by the name a person gave it, falling back to the id it was declared under. */
function label(item: ReviewItem): string {
  return item.stepName ?? item.stepId;
}


/**
 * Where this person's reviews reach them in Slack.
 *
 * Theirs and nobody else's — there is no endpoint that sets another person's,
 * and there should not be: an admin overriding a reviewer's route is how you
 * get approvals that reach nobody.
 *
 * `dm_only` deliberately does not silence the channel for everybody. It opts
 * one person out of watching it; a preference that could turn a channel off for
 * a team would be a preference that loses approvals.
 *
 * Renders nothing at all when Slack is not configured. A control whose every
 * option is "post to a Slack that does not exist" is worse than an absent one,
 * and the queue below it works exactly the same either way.
 */
function ReviewRoute() {
  const [route, setRoute] = useState<SlackReviewRoute | null>(null);
  const [configured, setConfigured] = useState(false);
  const [linked, setLinked] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .slackSettings()
      .then((settings) => {
        setConfigured(settings.configured);
        setLinked(settings.linked);
        setRoute(settings.preference.reviewRoute);
      })
      .catch(() => setConfigured(false));
  }, []);

  if (!configured || route === null) return null;

  const choose = async (next: SlackReviewRoute) => {
    const previous = route;
    setRoute(next);
    setBusy(true);
    setError(null);
    try {
      await api.setSlackReviewRoute(next);
    } catch (caught) {
      // Put it back. A control that shows the setting you asked for while the
      // server holds the old one is the worst of the three states.
      setRoute(previous);
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="studio-card">
      <div className="studio-card-head">
        <h2>Where these reach you</h2>
        <span className="spacer" />
        <span className="chip">Slack</span>
      </div>

      <div className="studio-card-body">
        <div className="row wrap">
          {ROUTES.map((option) => (
            <button
              key={option.value}
              className={route === option.value ? 'primary' : ''}
              onClick={() => void choose(option.value)}
              disabled={busy || route === option.value}
              title={option.description}
            >
              {option.label}
            </button>
          ))}
        </div>

        <p className="hint">{ROUTES.find((option) => option.value === route)!.description}</p>

        {/*
          The one state where a DM route is a trap. `dm_only` trades watching
          the channel for a DM, and with no linked Slack account there is nobody
          to DM — so somebody who set it would hear nothing and believe they
          were covered. Said before they pick, not after.
        */}
        {!linked && (
          <div className={route === 'channel' ? 'banner' : 'banner warn'}>
            <h3>Reef does not know your Slack account yet</h3>
            <p style={{ margin: 0 }}>
              {route === 'channel'
                ? 'The DM options need it. Message the bot in Slack once — it sends a link button, you sign in with Okta as usual, and both halves are then proven.'
                : 'So there is nobody to DM: you would hear nothing at all. Message the bot in Slack once to link your account, or switch back to the channel.'}
            </p>
          </div>
        )}

        {error && (
          <p className="hint" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

const ROUTES: ReadonlyArray<{ value: SlackReviewRoute; label: string; description: string }> = [
  {
    value: 'channel',
    label: 'In the channel',
    description:
      'Reviews go wherever the workflow sends them. Nothing lands in your DMs, and this screen is how you find them.',
  },
  {
    value: 'dm',
    label: 'Channel and a DM',
    description:
      "The channel still gets its copy; you also get one you cannot miss. Only for reviews you could actually decide.",
  },
  {
    value: 'dm_only',
    label: 'Just DM me',
    description:
      'You get the DM instead of watching the channel. Everybody else still gets theirs — this does not quieten the channel for your team.',
  },
];
